import React, { useEffect, useState, useContext } from "react";
import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
  query,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
} from "firebase/firestore";
import { db, auth as firebaseAuth } from "../firebase";
import { toast } from "react-toastify";
import { AuthContext } from "../context/AuthContext";
import UserFormModal from "../components/UserFormModal";
import { matchesUserSearch } from "../utils/userSearch";

// Backend API URL — uses proxy in dev (see package.json "proxy"), full URL in production
const API_BASE = process.env.REACT_APP_API_URL || "";

// MILESTONE 1: Subscription status badge component
const SubscriptionBadge = ({ status, planName, expiryDate }) => {
  const getStatusStyle = () => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'past_due':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'canceled':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'expired':
      case 'unpaid':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (!status || status === 'none') {
    return <span className="px-2 py-1 text-xs rounded border bg-gray-100 text-gray-500">Free</span>;
  }

  return (
    <div className="flex flex-col gap-1">
      <span className={`px-2 py-1 text-xs rounded border ${getStatusStyle()} font-medium`}>
        {status === 'active' ? '✅ Active' : 
         status === 'past_due' ? '⚠️ Past Due' :
         status === 'canceled' ? '🔄 Canceled' :
         status === 'expired' ? '❌ Expired' : status}
      </span>
      {planName && (
        <span className="text-xs text-gray-600">{planName}</span>
      )}
      {expiryDate && status === 'canceled' && (
        <span className="text-xs text-orange-600">Until: {formatDate(expiryDate)}</span>
      )}
    </div>
  );
};

export default function Users() {
  const { currentUser } = useContext(AuthContext);
  const [canManageUsers, setCanManageUsers] = useState(false);

  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    uid: "",
    profileImage: "",
    isVerified: false,
    isSubscriber: false,
  });
  const [searchText, setSearchText] = useState("");
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all"); // MILESTONE 1: Filter by subscription status

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const pageSize = 10;

  useEffect(() => {
    const loadAdminAccess = async () => {
      const firebaseUser = firebaseAuth.currentUser;
      if (!firebaseUser) {
        setCanManageUsers(false);
        return;
      }

      try {
        const tokenResult = await firebaseUser.getIdTokenResult();
        const isAdminClaim = tokenResult?.claims?.admin === true;
        setCanManageUsers(isAdminClaim || currentUser?.role === "admin");
      } catch (error) {
        console.error("Failed to read admin claims:", error);
        setCanManageUsers(currentUser?.role === "admin");
      }
    };

    loadAdminAccess();
  }, [currentUser]);

  const requireAdminAction = () => {
    if (!canManageUsers) {
      toast.error("Admin access required");
      return false;
    }
    return true;
  };

  // Helper: extract subscription details from a Firestore user document
  const extractUserData = (docSnapshot) => {
    const data = docSnapshot.data();
    const sub = data.subscription;

    // Parse expiry date from various Firestore formats
    let expiryDate = null;
    const periodEnd = sub?.currentPeriodEnd || sub?.current_period_end || sub?.endDate;
    if (periodEnd) {
      if (periodEnd.toDate) {
        // Firestore Timestamp
        expiryDate = periodEnd.toDate();
      } else if (typeof periodEnd === 'number') {
        expiryDate = new Date(periodEnd > 1e10 ? periodEnd : periodEnd * 1000);
      } else if (typeof periodEnd === 'string') {
        expiryDate = new Date(periodEnd);
      } else if (periodEnd._seconds !== undefined) {
        expiryDate = new Date(periodEnd._seconds * 1000);
      }
    }

    return {
      id: docSnapshot.id,
      ...data,
      // Subscription status from backend-driven data
      subscriptionStatus: sub?.status || null,
      planName: sub?.planName || sub?.plan || null,
      planId: sub?.planId || null,
      expiryDate: expiryDate
    };
  };

  useEffect(() => {
    // Use real-time listener for instant updates when subscriptions change
    const q = query(collection(db, "users"), orderBy("email"), limit(pageSize));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map(extractUserData);
      setUsers(list);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length === pageSize);
    }, (error) => {
      console.error("Error listening to users:", error);
      // Fallback to one-time fetch
      fetchUsers();
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    const q = query(collection(db, "users"), orderBy("email"), limit(pageSize));
    const snapshot = await getDocs(q);
    const list = snapshot.docs.map(extractUserData);
    setUsers(list);
    setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
    setHasMore(snapshot.docs.length === pageSize);
  };

  const loadMore = async () => {
    if (!lastDoc) return;
    const q = query(
      collection(db, "users"),
      orderBy("email"),
      startAfter(lastDoc),
      limit(pageSize)
    );
    const snapshot = await getDocs(q);
    const list = snapshot.docs.map(extractUserData);
    setUsers((prev) => [...prev, ...list]);
    setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
    setHasMore(snapshot.docs.length === pageSize);
  };

  const addUser = async () => {
    if (!requireAdminAction()) return;
    if (!form.email || !form.uid) return toast.error("Email and UID required");
    try {
      await setDoc(doc(db, "users", form.uid), {
        ...form,
        uid: form.uid,
      }, { merge: true });
      toast.success("User added");
      setForm({
        name: "",
        email: "",
        uid: "",
        profileImage: "",
        isVerified: false,
        isSubscriber: false,
      });
      fetchUsers();
    } catch (err) {
      toast.error("Error adding user");
    }
  };

  const updateUser = async (id) => {
    if (!requireAdminAction()) return;
    try {
      await updateDoc(doc(db, "users", id), form);

      // If verification status changed, also update Firebase Auth via backend API
      if (form.uid && form.isVerified !== undefined) {
        try {
          const adminToken = await firebaseAuth.currentUser?.getIdToken();
          const response = await fetch(`${API_BASE}/auth/admin-verify-user`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(adminToken ? { 'Authorization': `Bearer ${adminToken}` } : {}),
            },
            body: JSON.stringify({ uid: form.uid, isVerified: form.isVerified }),
          });
          if (response.ok) {
            const result = await response.json();
            if (result.success) {
              toast.success(form.isVerified ? "User verified ✅" : "User unverified");
            }
          }
        } catch (apiErr) {
          console.warn("Could not update Firebase Auth verification (backend may be unreachable):", apiErr);
        }
      }

      toast.success("User updated");
      fetchUsers();
    } catch (err) {
      toast.error("Error updating user");
    }
  };

  const deleteUser = async (id) => {
    if (!requireAdminAction()) return;
    if (!window.confirm("Are you sure you want to delete this user? This will remove the user completely and they can re-register.")) return;

    // Find the user to get their UID (Firebase Auth UID)
    const userToDelete = users.find(u => u.id === id);
    const uid = userToDelete?.uid || id;

    try {
      // 1. Delete from Firestore
      await deleteDoc(doc(db, "users", id));

      // 2. Delete from Firebase Auth via backend API
      try {
        const adminToken = await firebaseAuth.currentUser?.getIdToken();
        const response = await fetch(`${API_BASE}/auth/deleteUser/${uid}`, {
          method: "DELETE",
          headers: {
            'Content-Type': 'application/json',
            ...(adminToken ? { 'Authorization': `Bearer ${adminToken}` } : {}),
          },
        });

        if (response.ok) {
          toast.success("User fully deleted (Firestore + Firebase Auth) ✅");
        } else {
          toast.warn("Deleted from Firestore, but Firebase Auth deletion may have failed. User might not be able to re-register with same email.");
        }
      } catch (apiErr) {
        console.error("Backend delete API error:", apiErr);
        toast.warn("Deleted from Firestore, but couldn't reach backend to delete Firebase Auth account.");
      }

      fetchUsers();
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Error deleting user");
    }
  };

  const openEditUserModal = (user) => {
    setForm(user);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleModalSubmit = async () => {
    if (isEditing) {
      await updateUser(form.id);
    } else {
      await addUser();
    }
    setIsModalOpen(false);
  };
  // MILESTONE 1: Enhanced CSV export with subscription details
  const exportUsersToCSV = () => {
  if (!requireAdminAction()) return;
  if (users.length === 0) return toast.info("No users to export");

  const headers = [
    "Name",
    "Email",
    "UID",
    "Email Verified",
    "Subscription Status",
    "Plan Name",
    "Plan ID",
    "Access Until"
  ];
  const rows = users.map((user) => [
    user.name || "",
    user.email || "",
    user.uid || "",
    user.isVerified ? "Yes" : "No",
    user.subscriptionStatus || "Free",
    user.planName || "",
    user.planId || "",
    user.expiryDate ? user.expiryDate.toISOString().split('T')[0] : ""
  ]);

  const csvContent =
    [headers, ...rows]
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", "users.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  toast.success("Users exported successfully");
};


  // MILESTONE 1: Filter by search text AND subscription status
  const filteredUsers = users.filter((user) => {
    const matchesSearch = matchesUserSearch(user, searchText);
    
    if (filterStatus === "all") return matchesSearch;
    if (filterStatus === "active") return matchesSearch && user.subscriptionStatus === "active";
    if (filterStatus === "past_due") return matchesSearch && user.subscriptionStatus === "past_due";
    if (filterStatus === "canceled") return matchesSearch && user.subscriptionStatus === "canceled";
    if (filterStatus === "expired") return matchesSearch && (user.subscriptionStatus === "expired" || user.subscriptionStatus === "unpaid");
    if (filterStatus === "free") return matchesSearch && !user.subscriptionStatus;
    
    return matchesSearch;
  });

  return (
    <div className="p-6 max-w-5xl mx-auto black-white">
      <h1 className="text-2xl font-bold mb-4">User Manager</h1>

      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          placeholder="Search by email"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="flex-1 p-2 border rounded text-black"
        />
        {/* MILESTONE 1: Subscription status filter */}
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="p-2 border rounded text-black bg-white min-w-[160px]"
        >
          <option value="all">All Users</option>
          <option value="active">✅ Active Subscribers</option>
          <option value="past_due">⚠️ Past Due</option>
          <option value="canceled">🔄 Canceled</option>
          <option value="expired">❌ Expired/Unpaid</option>
          <option value="free">Free Users</option>
        </select>
      </div>

      {canManageUsers && (
        <div className="mb-6">
          <button
      onClick={exportUsersToCSV}
      className="bg-black text-white px-4 py-2 rounded"
    >
      Export Users
    </button>
        </div>
      )}

      <h2 className="text-xl font-semibold mb-3">User List</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm text-gray-900 bg-white rounded shadow">
          <thead>
            <tr>
              <th className="p-2 border-b">#</th>
              <th className="p-2 border-b">Name</th>
              <th className="p-2 border-b">Email</th>
              <th className="p-2 border-b">Verified</th>
              <th className="p-2 border-b">Subscription Status</th>
              <th className="p-2 border-b">Plan</th>
              <th className="p-2 border-b">Access Until</th>
              {canManageUsers && <th className="p-2 border-b">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user, i) => (
              <tr key={user.id} className="bg-white hover:bg-gray-100">
                <td className="p-2 border-b text-gray-800">{i + 1}</td>
                <td className="p-2 border-b text-gray-800">{user.name || '-'}</td>
                <td className="p-2 border-b text-gray-800 max-w-[200px] truncate">{user.email}</td>
                <td className="p-2 border-b text-gray-800 text-center">
                  {user.isVerified ? "✅" : "❌"}
                </td>
                {/* MILESTONE 1: Show subscription status with badge */}
                <td className="p-2 border-b">
                  <SubscriptionBadge 
                    status={user.subscriptionStatus} 
                    planName={user.planName}
                    expiryDate={user.expiryDate}
                  />
                </td>
                <td className="p-2 border-b text-gray-800 text-sm">
                  {user.planName || <span className="text-gray-400">-</span>}
                </td>
                <td className="p-2 border-b text-gray-800 text-sm">
                  {user.expiryDate ? (
                    <span className={user.expiryDate < new Date() ? 'text-red-600' : 'text-gray-600'}>
                      {user.expiryDate.toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                {canManageUsers && (
                  <td className="p-2 border-b space-x-2">
                    <button
                      onClick={() => openEditUserModal(user)}
                      className="bg-blue-500 text-white px-2 py-1 rounded text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteUser(user.id)}
                      className="bg-red-600 text-white px-2 py-1 rounded text-sm"
                    >
                      Delete
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div className="mt-4 text-center">
          <button
            onClick={loadMore}
            className="bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-600"
          >
            Load More
          </button>
        </div>
      )}

      {/* Modal */}
      <UserFormModal
        isOpen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        form={form}
        setForm={setForm}
        onSubmit={handleModalSubmit}
        isEdit={isEditing}
      />
    </div>
  );
}
